import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowUpFromLine,
  FileSpreadsheet,
  FileText,
  Loader2,
  LayoutTemplate,
  CornerUpLeft,
} from "lucide-react";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { reportsV2Export } from "@/config/endpoints";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/library/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import { useBuilderDirty } from "./custom/state/dirtyBridge";
import {
  keyFieldIdOfTab,
  useReportExportSnapshot,
  type ReportTabKey,
} from "./state/ReportsV2Context";

/**
 * Page title + Export, rendered into the white top app bar's left slot
 * (Header.tsx `isReportRoute` branch) — the "top white space". Mirrors the
 * leads/dynamics header-title pattern; no back arrow (this is a top-level page).
 *
 * Export downloads the ACTIVE tab's primary section as CSV/XLSX via the
 * server exporter (`GET /core/reports/v2/export?endpoint=…&format=…`) with
 * the exact filter params the page is currently showing (export snapshot
 * bridge — this component renders OUTSIDE <ReportsV2Provider>). Tabs whose
 * primary endpoint isn't in the v1 export registry (team → activity-summary,
 * revenue) explain themselves instead of 404ing.
 */

type TabExport = { endpoint: string; extra?: Record<string, string> };

/** tabKey → v1 export-registry endpoint (+ fixed extra params). Registry v1:
 *  kpi-summary, lead-distribution, dimension-performance, call-leaderboard,
 *  inactivity, velocity, field-insights, branch-performance. */
const TAB_EXPORT: Partial<Record<ReportTabKey, TabExport>> = {
  lead: { endpoint: "dimension-performance", extra: { dim: "source" } },
  // Promoted sections export the dimension they actually show. User
  // Performance stays omitted (button hides) rather than downloading a file that
  // isn't what's on screen — the exporter has no matching section for it yet.
  source: { endpoint: "dimension-performance", extra: { dim: "source" } },
  country: { endpoint: "dimension-performance", extra: { dim: "country" } },
  call: { endpoint: "call-leaderboard" },
  branch: { endpoint: "branch-performance" },
  fields: { endpoint: "field-insights" },
  // team (activity-summary) and revenue are NOT in the v1 registry — omitted
  // gracefully until the exporter learns them.
};

/** The export for a tab. A "By <Team>" key-field tab exports what By Country
 *  exports, counted by its field — the `keyFieldId` every country endpoint
 *  takes (the exporter passes params through to dimension-performance). */
function exportFor(tab: ReportTabKey): TabExport | undefined {
  const keyFieldId = keyFieldIdOfTab(tab);
  if (keyFieldId != null)
    return {
      endpoint: "dimension-performance",
      extra: { dim: "country", keyFieldId: String(keyFieldId) },
    };
  return TAB_EXPORT[tab];
}

const filenameFromDisposition = (header: unknown): string | null => {
  if (typeof header !== "string") return null;
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(header);
  return m ? decodeURIComponent(m[1]).replace(/"/g, "") : null;
};

export default function ReportHeaderTitle() {
  const snapshot = useReportExportSnapshot();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<"csv" | "xlsx" | null>(null);
  // ?custom=1 = the custom report builder is on screen (ReportsV2 swaps its
  // body). The Export popover targets the tab that ISN'T showing then, so it
  // hides while custom is open — the builder ships its own CSV export.
  const [searchParams, setSearchParams] = useSearchParams();
  const customOpen = Boolean(searchParams.get("custom"));
  // The builder publishes its unsaved-changes flag through a module bridge
  // (this component renders outside the builder's tree). Back must consult it:
  // deleting the params below silently destroys any in-flight edits.
  const builderDirty = useBuilderDirty();
  const [confirmBackOpen, setConfirmBackOpen] = useState(false);

  const exitCustom = () =>
    setSearchParams((p) => {
      p.delete("custom");
      p.delete("report");
      return p;
    });

  const exportable = snapshot ? exportFor(snapshot.activeTab) : undefined;

  const download = async (format: "csv" | "xlsx") => {
    if (!snapshot || !exportable || downloading) return;
    setDownloading(format);
    try {
      const res = await axiosInstance.get(reportsV2Export, {
        params: {
          endpoint: exportable.endpoint,
          format,
          ...snapshot.params,
          ...(exportable.extra ?? {}),
        },
        responseType: "blob",
      });
      const blob = res.data as Blob;
      const filename =
        filenameFromDisposition(res.headers?.["content-disposition"]) ??
        `${exportable.endpoint}-${new Date().toISOString().slice(0, 10)}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch (error: any) {
      // Blob responses hide the JSON error body — read it back out so a 409
      // (export busy/too large) surfaces the SERVER's message, not a guess.
      let message = "Export failed — please try again.";
      const data = error?.response?.data;
      try {
        if (data instanceof Blob) {
          const parsed = JSON.parse(await data.text());
          message = parsed?.message || message;
        } else if (typeof data?.message === "string") {
          message = data.message;
        }
      } catch {
        /* keep fallback copy */
      }
      toast.error(message);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="min-w-0">
        <p className="text-[20px] font-semibold tracking-[-0.5px] text-[#0c211b]">
          Reports
        </p>
        <p className="text-[11px] font-normal tracking-[-0.22px] text-[#697571]">
          Pipeline, calls & conversion analytics
        </p>
      </div>

      {!customOpen && (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Export this report"
            className="flex items-center gap-1.5 rounded-full border border-[#d6ebe3] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#218760]"
          >
            <ArrowUpFromLine className="size-3.5" /> Export
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[260px] p-2">
          {!snapshot ? (
            <p className="px-2 py-1.5 text-[12px] text-[#697571]">
              Switch to the new Reports view to export the current tab.
            </p>
          ) : !exportable ? (
            <p className="px-2 py-1.5 text-[12px] text-[#697571]">
              Export isn&apos;t available for this tab yet — its data service
              doesn&apos;t support downloads in this version.
            </p>
          ) : (
            <div className="flex flex-col gap-0.5">
              <p className="px-2 pb-1 pt-0.5 text-[11px] text-[#9ba3a0]">
                Downloads the current tab with the active filters applied.
              </p>
              <button
                type="button"
                onClick={() => download("csv")}
                disabled={downloading !== null}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-[#051912] transition-colors hover:bg-[#f0f6f3] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading === "csv" ? (
                  <Loader2 className="size-4 animate-spin text-[#218760]" />
                ) : (
                  <FileText className="size-4 text-[#218760]" />
                )}
                Download current tab (CSV)
              </button>
              <button
                type="button"
                onClick={() => download("xlsx")}
                disabled={downloading !== null}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-[#051912] transition-colors hover:bg-[#f0f6f3] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading === "xlsx" ? (
                  <Loader2 className="size-4 animate-spin text-[#218760]" />
                ) : (
                  <FileSpreadsheet className="size-4 text-[#218760]" />
                )}
                Download current tab (XLSX)
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
      )}

      {/* Custom report builder toggle — same pill family as Export. Back
          clears BOTH params so the gallery/builder position never leaks into
          the next visit. */}
      {customOpen ? (
        <button
          type="button"
          title="Back to the standard reports"
          onClick={() => (builderDirty ? setConfirmBackOpen(true) : exitCustom())}
          className="flex items-center gap-1.5 rounded-full border border-[#218760]/40 bg-[#218760]/10 px-3 py-1.5 text-[12px] font-medium text-[#218760] transition-colors hover:bg-[#218760]/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#218760]"
        >
          <CornerUpLeft className="size-3.5" /> Back to reports
        </button>
      ) : (
        <button
          type="button"
          title="Build a custom report"
          onClick={() =>
            setSearchParams((p) => {
              p.set("custom", "1");
              return p;
            })
          }
          className="flex items-center gap-1.5 rounded-full border border-[#d6ebe3] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#218760]"
        >
          <LayoutTemplate className="size-3.5" /> Custom
        </button>
      )}

      {/* Same confirm the builder's own Cancel shows — the two exits must
          never differ in how carefully they treat unsaved work. */}
      <Dialog open={confirmBackOpen} onOpenChange={setConfirmBackOpen}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-medium tracking-[-0.3px] text-[#051912]">
              Discard changes?
            </DialogTitle>
            <DialogDescription className="text-[12px] text-[#697571]">
              This report has unsaved changes. Leaving now throws them away.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-1.5">
            <button
              type="button"
              onClick={() => setConfirmBackOpen(false)}
              className="rounded-full border border-[#d6ebe3] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1]"
            >
              Keep editing
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmBackOpen(false);
                exitCustom();
              }}
              className="rounded-full bg-[#da2a46] px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-[#b82239]"
            >
              Discard
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
